<script setup lang="ts">
import { computed } from 'vue'
import type { ComboSceneLayer } from '../lib/comboScene'

/**
 * Составная картинка комбо: позиции набора на своих местах вместо одной загруженной картинки.
 *
 * Слои приходят готовыми — компонент только рисует. Кто и как выбирает позицию слота,
 * решает вызывающий: плитка меню показывает набор по умолчанию, конструктор — то, что
 * гость собрал прямо сейчас, корзина — то, что он заказал.
 *
 * Ни одного расставленного слота — показываем обычную картинку комбо: раскладку админ
 * может ещё не сделать, и комбо не должно остаться без картинки вовсе.
 */
const props = defineProps<{
  layers: ComboSceneLayer[]
  /** Картинка самого комбо — фоллбэк, пока слоты не расставлены */
  fallbackImg?: string | null
  /** Тени под позициями: уместны в большом герое, в мелкой плитке только грязнят */
  shadows?: boolean
}>()

const hasScene = computed(() => props.layers.length > 0)

const layerStyle = (layer: ComboSceneLayer) => ({
  left: `${layer.layout.x}%`,
  top: `${layer.layout.y}%`,
  height: `${layer.layout.size}%`,
  zIndex: layer.layout.z,
})
</script>

<template>
  <div class="combo-scene">
    <div v-if="hasScene" class="combo-scene__stage">
      <TransitionGroup name="combo-scene-layer">
        <img
          v-for="layer in layers"
          :key="layer.key"
          :src="layer.img"
          class="combo-scene__layer"
          :class="{ 'combo-scene__layer--shadow': shadows }"
          :style="layerStyle(layer)"
          alt=""
          loading="lazy"
        >
      </TransitionGroup>
    </div>

    <img v-else-if="fallbackImg" :src="fallbackImg" class="combo-scene__full" alt="" loading="lazy">
  </div>
</template>

<style scoped lang="scss">
.combo-scene {
  @apply relative w-full h-full;

  &__stage {
    @apply relative w-full h-full;
  }

  // Центр слоя в (x, y) — иначе при смене размера позиция «убегала» бы вправо-вниз
  &__layer {
    position: absolute;
    transform: translate(-50%, -50%);
    object-fit: contain;
    max-width: none;
    user-select: none;
    pointer-events: none;
  }

  &__layer--shadow {
    filter: drop-shadow(2px 4px 8px rgb(0 0 0 / 18%));
  }

  &__full {
    @apply w-full h-full object-contain;
  }
}

// Замена позиции в слоте: слой меняется на месте, сцена не дёргается
.combo-scene-layer-enter-active,
.combo-scene-layer-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.combo-scene-layer-enter-from,
.combo-scene-layer-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.92);
}

.combo-scene-layer-leave-active {
  position: absolute;
}
</style>
